import { keyframes } from "@mui/material";

/** Spin the Wheel visual language — gold neon hub, magenta host, casino black */
export const SPIN = {
  gold: "#ffc107",
  goldDeep: "#e8a800",
  goldSoft: "#ffe082",
  hot: "#ff6b3d",
  hotDeep: "#c43a1a",
  live: "#ff2d8a",
  liveDeep: "#c2185b",
  teal: "#4ecdc4",
  ink: "#070708",
  panel: "rgba(16, 16, 18, 0.78)",
  panelBorder: "rgba(255,255,255,0.08)",
  muted: "rgba(255,255,255,0.62)",
  fontDisplay: '"Syne", "Avenir Next", system-ui, sans-serif',
  fontBody: '"DM Sans", "Helvetica Neue", system-ui, sans-serif',
};

export const spinFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

export const spinPulse = keyframes`
  0%, 100% { opacity: 0.55; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.04); }
`;

export const spinFadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const spinOrbDrift = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(2%, -3%) scale(1.05); }
  66% { transform: translate(-2%, 2%) scale(0.97); }
`;

export const spinGlow = keyframes`
  0%, 100% { filter: drop-shadow(0 0 10px rgba(255,193,7,0.35)); }
  50% { filter: drop-shadow(0 0 22px rgba(255,193,7,0.7)); }
`;

export const spinSlowRotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const spinPageSx = {
  height: "100dvh",
  maxHeight: "100%",
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  position: "relative",
  fontFamily: SPIN.fontBody,
  background:
    "radial-gradient(ellipse 70% 45% at 18% 12%, rgba(255,193,7,0.18) 0%, transparent 55%)," +
    "radial-gradient(ellipse 55% 40% at 88% 18%, rgba(255,45,138,0.16) 0%, transparent 50%)," +
    "radial-gradient(ellipse 50% 35% at 70% 88%, rgba(255,107,61,0.12) 0%, transparent 55%)," +
    "radial-gradient(ellipse 40% 30% at 8% 78%, rgba(78,205,196,0.08) 0%, transparent 50%)," +
    "linear-gradient(165deg, #050506 0%, #0c0d10 38%, #0a0a0c 72%, #050506 100%)",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    opacity: 0.4,
    backgroundImage:
      "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.045) 1px, transparent 0)",
    backgroundSize: "28px 28px",
    maskImage:
      "radial-gradient(ellipse 75% 65% at 50% 40%, #000 15%, transparent 78%)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    width: "55vmax",
    height: "55vmax",
    top: "-18%",
    left: "50%",
    transform: "translateX(-50%)",
    pointerEvents: "none",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(255,193,7,0.1) 0%, rgba(255,45,138,0.06) 42%, transparent 68%)",
    animation: `${spinOrbDrift} 14s ease-in-out infinite`,
    filter: "blur(2px)",
  },
};

export const spinAppBarSx = {
  bgcolor: "rgba(7,7,8,0.55)",
  color: "#fff",
  backdropFilter: "blur(16px)",
  borderBottom: "1px solid rgba(255,255,255,0.07)",
  boxShadow: "none",
  flexShrink: 0,
};

export const spinModeTileSx = (accent) => ({
  height: "100%",
  cursor: "pointer",
  textAlign: "left",
  p: { xs: 2.5, sm: 3 },
  borderRadius: "20px",
  background: SPIN.panel,
  backdropFilter: "blur(18px)",
  border: `1px solid ${SPIN.panelBorder}`,
  position: "relative",
  overflow: "hidden",
  transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1), border-color 0.25s, box-shadow 0.35s",
  animation: `${spinFadeUp} 0.55s ease both`,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: `linear-gradient(90deg, ${accent}, transparent)`,
  },
  "&:hover": {
    transform: "translateY(-6px)",
    borderColor: `${accent}55`,
    boxShadow: `0 18px 48px rgba(0,0,0,0.45), 0 0 0 1px ${accent}22`,
  },
  "&:active": {
    transform: "translateY(-2px)",
  },
});

export const spinPrimaryBtnSx = {
  py: { xs: 1, sm: 1.25 },
  px: { xs: 2.5, sm: 4.5 },
  minHeight: { xs: 42, sm: 50 },
  fontFamily: SPIN.fontDisplay,
  fontWeight: 800,
  fontSize: { xs: "0.9rem", sm: "1rem" },
  letterSpacing: "0.04em",
  textTransform: "none",
  borderRadius: "14px",
  color: "#0a0a0a",
  background: `linear-gradient(135deg, ${SPIN.gold} 0%, ${SPIN.goldDeep} 100%)`,
  boxShadow: "0 10px 32px rgba(255,193,7,0.28)",
  "&:hover": {
    background: `linear-gradient(135deg, ${SPIN.goldSoft} 0%, ${SPIN.gold} 100%)`,
    boxShadow: "0 14px 40px rgba(255,193,7,0.38)",
  },
  "&.Mui-disabled": {
    background: "rgba(255,193,7,0.22)",
    color: "rgba(255,255,255,0.35)",
    boxShadow: "none",
  },
};

export const spinHotBtnSx = {
  ...spinPrimaryBtnSx,
  color: "#1a0804",
  background: `linear-gradient(135deg, #ff8a5c 0%, ${SPIN.hot} 50%, ${SPIN.hotDeep} 100%)`,
  boxShadow: "0 10px 32px rgba(255,107,61,0.28)",
  "&:hover": {
    background: "linear-gradient(135deg, #ff9b72 0%, #ff7a4d 50%, #d44822 100%)",
    boxShadow: "0 14px 40px rgba(255,107,61,0.38)",
  },
  "&.Mui-disabled": {
    background: "rgba(255,107,61,0.22)",
    color: "rgba(255,255,255,0.35)",
    boxShadow: "none",
  },
};

export const spinGhostBtnSx = {
  textTransform: "none",
  fontFamily: SPIN.fontBody,
  fontWeight: 600,
  color: SPIN.muted,
  borderColor: "rgba(255,255,255,0.14)",
  borderRadius: "12px",
  "&:hover": {
    borderColor: "rgba(255,255,255,0.28)",
    bgcolor: "rgba(255,255,255,0.04)",
    color: "#fff",
  },
};

export const spinPanelSx = {
  maxWidth: 440,
  mx: "auto",
  p: { xs: 3, sm: 4 },
  borderRadius: "20px",
  background: SPIN.panel,
  border: `1px solid ${SPIN.panelBorder}`,
  backdropFilter: "blur(16px)",
  textAlign: "center",
};

export const spinTitleGradientSx = {
  fontFamily: SPIN.fontDisplay,
  fontWeight: 800,
  letterSpacing: "0.04em",
  whiteSpace: "nowrap",
  background: `linear-gradient(90deg, ${SPIN.gold}, ${SPIN.hot}, ${SPIN.live})`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
};
